import { getPval } from "@/utils/statistics.js";
import { calcRange, commafy } from "@/plots/utils.js";
import { getString } from "@/utils/string.js";

import * as echarts from "echarts";

export function buildManhattanPlot(
  plotContainer,
  pureData,
  manhattanClickHandler
) {
  let chart = echarts.init(plotContainer);
  let data = [];
  let minPos = 1e13;
  let maxPos = -1;
  let minLogP = 1e13;
  let maxLogP = -1;

  pureData.forEach((item) => {
    minPos = Math.min(minPos, item["cumulativePOS"]);
    maxPos = Math.max(maxPos, item["cumulativePOS"]);
    minLogP = Math.min(minLogP, Math.abs(item["X"]));
    maxLogP = Math.max(maxLogP, Math.abs(item["X"]));
  });

  let chromosomeRanges = new Map();
  pureData.forEach((item) => {
    let chromosome = parseInt(item["ID"].split("_")[0]) - 1;
    if (!chromosomeRanges.has(chromosome)) {
      chromosomeRanges.set(chromosome, { min: 1e13, max: -1 });
    }
    let range = chromosomeRanges.get(chromosome);
    range.min = Math.min(range.min, item["cumulativePOS"]);
    range.max = Math.max(range.max, item["cumulativePOS"]);
  });

  let chromosomeData = [];

  pureData.forEach((item) => {
    let chromosome = parseInt(item["ID"].split("_")[0]) - 1;
    let range = chromosomeRanges.get(chromosome);
    let relativePos =
      (item["cumulativePOS"] - range.min) / (range.max - range.min);
    let xPos = chromosome + relativePos;
    let itemObject = [
      xPos,
      Math.abs(item["X"]),
      item["ID"],
      parseInt(item["ID"].split("_")[1]),
      Math.abs(item["X"]),
      item["RSID"],
      item["GENE"],
      item["IMPACT"],
      item["Consequence"],
      item["PX"],
      item["pi"],
      item["FDR"],
    ];

    if (!chromosomeData[chromosome]) {
      chromosomeData[chromosome] = [];
    }

    chromosomeData[chromosome].push(itemObject);
  });

  const colors = ["#8996A0", "#1E1E1E"];

  let series = [];
  chromosomeData.forEach((chromosome, index) =>
    series.push({
      data: chromosome,
      type: "scatter",
      symbolSize: 4,
      itemStyle: {
        opacity: 0.98,
        color: colors[index % colors.length],
        borderWidth: 1,
      },
      label: {
        precision: 1,
      },
      blendMode: "source-over",
      large: true,
      largeThreshold: 10,
    })
  );

  let option = {
    title: {
      text: " Interactive Manhattan Plot Highlighting the Signature of Natural Selection in the Human Genome",
      left: "center",
      textStyle: {
        fontFamily: "Ubuntu",
        fontSize: 15,
      },
    },
    xAxis: {
      show: true,
      axisLabel: {
        formatter: (value, index) => {
          if (value <= 21 && value >= 0 && value % 1 === 0) {
            return value + 1;
          } else {
            return "";
          }
        },
        interval: 1,
        margin: 5,
      },
      axisTick: {
        show: false,
      },
      splitNumber: 22,
      name: "Chromosome",
      nameTextStyle: {
        fontSize: 15,
        fontFamily: "Ubuntu"
      },
      nameLocation: "center",
      axisLine: {
        show: true,
        onZero: false,
      },
      nameGap: 25,
      splitLine: {
        show: false,
        lineStyle: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
    yAxis: {
      type: "value",
      min: 4,
      show: true,
      axisLine: {
        show: true,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
      axisLabel: {
        formatter: (value) => {
          return `${Math.round(value)}`
        }
      },
      name: "Magnitude of Change over Time ( |X| )",
      nameTextStyle: {
        fontSize: 12,
        fontFamily: "Ubuntu"
      },
      nameLocation: "center",
      nameGap: 30,
    },
    grid: {
      left: 45,
      top: 40,
      right: 25,
      bottom: 40,
    },
    toolbox: {
      right: 30,
      top: 0,
      itemSize: 20,
      feature: {
        dataZoom: {
          iconStyle: {
            opacity: 0,
          },
          icon: null,
          title: { zoom: "", back: "" },
          brushStyle: {
            color: "rgba(165, 28, 48, 0.25)",
          },
        },
        restore: {
          icon: "image://data:image/png;base64,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",
          title: "",
        },
      },
    },
    series: series,
    tooltip: {
      trigger: "item",
      textStyle: {
        fontSize: 11,
      },
      formatter: function (params) {
        return `<b>Variant ID: </b>${params.data[2]}<br/><b>RSID: </b>${getString(params.data[5])}<br/><b>Gene: </b>${getString(params.data[6])}<br/><b>Impact: </b>${getString(params.data[7])}<br/><b>Consequence: </b>${getString(params.data[8])}<br/><b>|X|: </b>${params.data[1].toFixed(3)}<br/><b>P value: </b>${getPval(params.data[9])}<br/><b>Posterior (<span style="font-family: 'Times New Roman', serif; font-size: 1.2em;">π</span>): </b>${params.data[10].toFixed(2)}<br/><b>FDR: </b>${params.data[11].toFixed(2)}`;

      },
    },
  };
  chart.setOption(option);
  chart.on("click", manhattanClickHandler);
  chart.on("restore", () => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "dataZoomSelect",
      dataZoomSelectActive: true,
    });
  });
  chart.dispatchAction({
    type: "takeGlobalCursor",
    key: "dataZoomSelect",
    dataZoomSelectActive: true,
  });
}
